'use client';

import { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Bell, Loader2, MessageSquare, ShoppingBag, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { haptics } from '@/lib/native';
import { cn } from '@/lib/utils';

const REASONS = [
  { icon: Tag, label: 'Offers on your listings', hint: 'Know within seconds, not at the next inbox check' },
  { icon: MessageSquare, label: 'Messages from buyers', hint: 'Reply while they are still deciding' },
  { icon: ShoppingBag, label: 'Orders and pickups', hint: 'New orders and confirmed pickup times' },
];

export interface PushPromptSheetProps {
  open: boolean;
  /** Resolves to the permission outcome so the parent can stop asking. */
  onEnable: () => Promise<void>;
  onDismiss: () => void;
}

/**
 * The soft ask that goes *before* the iOS permission alert.
 *
 * iOS gives an app exactly one shot at the system prompt: a "Don't Allow" there
 * can only be undone in Settings. So this sheet does the persuading, and the
 * system alert is only reached by someone who already said yes — which is also
 * why "Not now" is a plain dismissal, not a refusal.
 */
export function PushPromptSheet({ open, onEnable, onDismiss }: PushPromptSheetProps) {
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (open) void haptics.light();
    else setWorking(false);
  }, [open]);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !working) onDismiss();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-[71] flex flex-col gap-5 rounded-t-2xl border-t bg-background p-5 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom duration-200',
            'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border',
            'sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0',
          )}
          style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30 sm:hidden" />

          <div className="flex flex-col items-center gap-3 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
              <Bell className="h-6 w-6 text-primary" />
            </span>
            <DialogPrimitive.Title className="text-lg font-semibold leading-tight tracking-tight">
              Get notified about offers, messages and orders
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground">
              We&apos;ll send these to your phone instead of your inbox.
            </DialogPrimitive.Description>
          </div>

          <ul className="space-y-3">
            {REASONS.map((reason) => (
              <li key={reason.label} className="flex items-start gap-3">
                <reason.icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{reason.label}</span>
                  <span className="block text-xs text-muted-foreground">{reason.hint}</span>
                </span>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              className="w-full"
              disabled={working}
              onClick={async () => {
                setWorking(true);
                void haptics.medium();
                await onEnable();
              }}
            >
              {working && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Turn on notifications
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground"
              disabled={working}
              onClick={() => {
                void haptics.selection();
                onDismiss();
              }}
            >
              Not now
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
